import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export default function ProblemReframe() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const color = useTransform(
    scrollYProgress,
    [0, 0.4, 0.7, 1],
    ["#FFB800", "#ffffff", "#00F0FF", "#00F0FF"]
  );
  const xShift = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section
      ref={ref}
      id="reframe"
      data-testid="problem-reframe"
      className="relative bg-[#050505] py-32 md:py-48 overflow-hidden border-t border-white/5"
    >
      <div className="absolute inset-0 precision-grid opacity-30" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-5 space-y-5">
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-[#FFB800]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">
              Symptom / 02
            </span>
          </div>
          <h3 className="font-display text-xl md:text-2xl text-zinc-400 leading-tight tracking-tight">
            Most teams are trying to
            <br />
            <span className="text-white">out-hire a broken process.</span>
          </h3>
          <ul className="space-y-4 pt-4" data-testid="problem-list">
            {[
              "Reps spending 60% of time on research, not selling",
              "Buying signals missed because no one's watching",
              "Fragmented tools with no source of truth",
              "Guess-based sequencing, inconsistent volume",
              "Inbound leads going cold before anyone follows up",
              "CRM data too dirty to trust for forecasting",
              "Pipeline built on volume, not intent",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 font-body text-[17px] text-zinc-400">
                <span className="mt-2.5 h-1 w-3 bg-[#FFB800]/60 shrink-0" />
                <span className="line-through decoration-white/60 underline-offset-4 decoration-1">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="col-span-12 md:col-span-7 md:pl-10 md:border-l border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-[#00F0FF]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">
              Reframe / 02
            </span>
          </div>

          <motion.h2
            style={{ color, x: xShift }}
            className="font-display font-bold uppercase tracking-[-0.04em] leading-[0.88] text-[44px] sm:text-[64px] md:text-[76px] lg:text-[92px]"
            data-testid="reframe-statement"
          >
            You don&apos;t
            <br />
            need more reps.
          </motion.h2>
          <h2 className="mt-2 font-display font-bold uppercase tracking-[-0.04em] leading-[0.88] text-[44px] sm:text-[64px] md:text-[76px] lg:text-[92px] text-white">
            You need
            <br />
            <span className="text-[#CCFF00]">a system.</span>
          </h2>

          <p className="mt-10 font-body text-zinc-400 max-w-[56ch] text-[15px] md:text-[17px] leading-relaxed">
            Adding headcount to a broken process scales chaos, not revenue. We
            architect precision data engines that replace manual SDR effort with
            autonomous intelligence — running on your stack, reporting into your
            CRM, and compounding weekly.
          </p>
        </div>
      </div>
    </section>
  );
}
